import { useState, useEffect } from "react";
import { Chart } from "primereact/chart";
import { getCampaignAnalyticsOptions } from "../../shared/utility";
import ButtonSpinner from "../../components/controls/ButtonSpinner";

export default function CampaignAnalytics(props) {
  const {
    analyticsData,
    campaignId,
    generateCampaignReport,
    reportInProgress,
  } = props;
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    if (analyticsData) {
      const options = getCampaignAnalyticsOptions(analyticsData);

      setChartData(analyticsData);
      setChartOptions(options);
    }
  }, [analyticsData]);

  if (!analyticsData) {
    return (
      <div className="bg-white shadow-lg rounded-xl p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Campaign Analytics
        </h3>
        <p className="text-gray-600">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-lg rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Campaign Analytics
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Distribution overview and activity summary
          </p>
        </div>
        <button
          onClick={() => generateCampaignReport(campaignId)}
          disabled={reportInProgress}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-md shadow-sm font-medium transition-colors"
        >
          <ButtonSpinner spin={reportInProgress} text="Generate PDF Report" />
        </button>
      </div>

      {/* Chart Content */}
      <div className="p-6">
        <div className="h-96">
          <Chart type="bar" data={chartData} options={chartOptions} />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 px-6 pb-6 sm:grid-cols-3">
        <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4 border border-pink-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-pink-600">Visits</p>
              <p className="text-2xl font-bold text-pink-900">
                {analyticsData?.datasets?.[0]?.data?.[0] ?? 0}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-pink-500 flex items-center justify-center">
              <svg
                className="h-6 w-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">
                Samples Distributed
              </p>
              <p className="text-2xl font-bold text-blue-900">
                {analyticsData?.datasets?.[0]?.data?.[1] ?? 0}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center">
              <svg
                className="h-6 w-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">
                Products Sold
              </p>
              <p className="text-2xl font-bold text-yellow-900">
                {analyticsData?.datasets?.[0]?.data?.[2] ?? 0}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-yellow-500 flex items-center justify-center">
              <svg
                className="h-6 w-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
